import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import SmoothSlider from "Components/SmoothSlider/SmoothSlider";
import "./InformationDescriptionSide.css";

import { Mail, Phone } from "lucide-react";
import { motion } from "framer-motion";
import {
  PAccordion,
  PDivider,
  PTable,
  PTableBody,
  PTableCell,
  PTableRow,
} from "@porsche-design-system/components-react";
import { useTranslation } from "react-i18next";

import VitalInfoComponent from "Components/VitalInfoComponent/VitalInfoComponent";
import CallToBook from "Components/CallToBook/CallToBook";
import {
  spacingFluidLarge,
  spacingFluidMedium,
  spacingFluidSmall,
  spacingFluidXSmall,
} from "@porsche-design-system/components-js/styles";
import { ToursContext } from "MainApp/assets/utils/context/TourContext";
import { SkeletonImagePreload } from "MainApp/assets/visual/styles/globalStyles/globalJsxStyles";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { FaWhatsapp } from "react-icons/fa";
// import useAxios from "MainApp/assets/utils/hooks/useAxios";

const InformationDescriptionSide = ({ tour, isPhone }) => {
  // const axiosInstance = useAxios();
  const { axiosInstance, token, isAuthReady, userData, optionalFields } =
    useContext(AuthContext);

  const { t, i18n } = useTranslation("tourInfo");

  const { getImagesByTourId, setImagesByTourId } = useContext(ToursContext);
  const [images, setImages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [imageLoaded, setImageLoaded] = useState(false);

// helpers i18n (arriba del componente o en un utils)
const pickI18n = (objOrStr, preferredLocales) => {
  if (!objOrStr) return "";
  if (typeof objOrStr === "string") return objOrStr;
  for (const lc of preferredLocales) {
    const v = objOrStr?.[lc];
    if (typeof v === "string" && v.trim()) return v;
  }
  const first = Object.values(objOrStr).find(v => typeof v === "string" && v.trim());
  return first || "";
};
const preferredLocalesFromI18n = (i18n) => {
  const out = [];
  const lang = i18n?.language || i18n?.resolvedLanguage;
  if (lang) out.push(lang.split("-")[0], lang);
  const fb = i18n?.options?.fallbackLng;
  if (Array.isArray(fb)) out.push(...fb);
  else if (typeof fb === "string") out.push(fb);
  else if (fb && typeof fb === "object") out.push(...Object.values(fb).flat());
  out.push("default");
  return [...new Set(out.filter(Boolean))];
};

const preferredLocales = useMemo(
  () => preferredLocalesFromI18n(i18n),
  [i18n.language]
);

const descriptionText = useMemo(
  () => pickI18n(tour?.descriptionI18n ?? tour?.description, preferredLocales),
  [tour, preferredLocales]
);

  const [accordionStates, setAccordionStates] = useState({
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
  });

  const handleAccordionUpdate = useCallback((index, e) => {
    setAccordionStates((prev) => ({ ...prev, [index]: e.detail.open }));
  }, []);

  const cachedImages = useMemo(
    () => getImagesByTourId(tour.id),
    [tour.id, getImagesByTourId]
  );

  useEffect(() => {
    if (tour && isAuthReady && token && axiosInstance) {
      if (cachedImages) {
        setImages(cachedImages);
      } else {
        fetchImages();
      }
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, axiosInstance, token, isAuthReady, cachedImages]);

  const fetchImages = async () => {
    try {
      // console.log("👉 tour.id:", tour.id);
      // console.log("👉 tour:", tour);
      const params = { tourUUID: tour.id };
      // console.log(params);
      // console.log(axiosInstance);
      const response = await axiosInstance.get("/toursAPI/get-images", {
        params,
        mode: "cors",
      });
      setImages(response.data);
      setImagesByTourId(tour.id, response.data);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  const fetchLocations = async () => {
    const params = { tourUUID: tour.id };
    try {
      const response = await axiosInstance.get("/toursAPI/get-locations", {
        params,
      });
      setLocations(response.data);
      console.log("response Locations", response.data);
    } catch (error) {
      console.log("response ERROR Locations", error);
    }
  };

  if (!tour) return null;

  const rotatedStyle = {
    // minHeight: isRotated ? "" : "40svh",
    minHeight: "40svh",
  };

  // Obtener los contenidos de traducción como objetos
  const infoContent =
    t("accordion.info.content", { returnObjects: true }) || {};

  const legalContent =
    t("accordion.legal.content", { returnObjects: true }) || {};

  const rotatedStyleSkeleton = {
    maxHeight: "40svh",
    width: "90%",
  };
  const custom = {
    height: "100%",
    position: "absolute",
    zIndex: "200",
    top: "50%",
    left: "50%",
    transform: "translate(-50%,-50%)",
  };

  const hasMultipleDurations =
    Array.isArray(tour?.durations) && tour.durations.length > 1;
  // Helpers (puedes ponerlos arriba del componente o en un utils.ts)

  const normalizeForTel = (s) => {
    const trimmed = s.trim();
    const keepPlus = trimmed.startsWith("+");
    const digits = trimmed.replace(/\D/g, "");
    return (keepPlus ? "+" : "") + digits; // tel: admite '+'
  };

  const normalizeForWa = (s) => {
    // wa.me requiere SOLO dígitos con prefijo de país, sin '+'
    return s.replace(/\D/g, "");
  };

  const normalizeEmail = (s) => s.trim().replace(/\s/g, "");

  return (
    <div
      className="informationDescriptionSide"
      style={{ gap: isPhone ? "" : spacingFluidSmall }}
    >
      {isPhone && <CallToBook isSimple={true} isPhone={isPhone} tour={tour} />}

      <div
        className="infoSliderContainer"
        style={{
          marginTop: isPhone ? spacingFluidLarge : "",
          marginBottom: isPhone ? spacingFluidSmall : "",
          marginLeft: isPhone ? spacingFluidSmall : spacingFluidMedium,
          marginRight: isPhone ? spacingFluidSmall : spacingFluidMedium,
          ...rotatedStyle,
        }}
      >
        {!imageLoaded && (
          <SkeletonImagePreload
            style={{
              ...rotatedStyleSkeleton,
              ...custom,
            }}
          />
        )}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="smoothContainerInfoSide"
          style={{ rotatedStyle }}
        >
          <SmoothSlider
            images={images}
            imageLoaded={imageLoaded}
            setImageLoaded={setImageLoaded}
          />
        </motion.div>
      </div>

      <PDivider direction={"vertical"} className="vDivider" />

      <div
        className="infoArea"
        style={{ paddingTop: isPhone ? spacingFluidSmall : "" }}
      >
        {!hasMultipleDurations ?? (
          <VitalInfoComponent tour={tour} isMulti={true} />
        )}
        <PDivider
          className="hDivider"
          style={{
            marginTop: isPhone ? spacingFluidSmall : "",
            marginBottom: isPhone ? spacingFluidXSmall : "",
          }}
        />

        {/* Accordion de Descripción */}
        {optionalFields?.description && (
          <PAccordion
            heading={t("accordion.desc.head") || "Loading..."}
            open={accordionStates[1]}
            onUpdate={(e) => handleAccordionUpdate(1, e)}
          >
            <PTable caption={t("accordion.desc.caption") || "Loading..."}>
              <PTableBody>
                <PTableRow>
                  {/* <PTableCell multiline={true} > */}
                  <div className="whiteSpaceBreak">{descriptionText}</div>
                  {/* </PTableCell> */}
                </PTableRow>
              </PTableBody>
            </PTable>
          </PAccordion>
        )}

        {/* Accordion de Localizaciones */}
        {optionalFields?.localizations && (
          <PAccordion
            heading={t("accordion.loc.head") || "Loading..."}
            open={accordionStates[2]}
            onUpdate={(e) => handleAccordionUpdate(2, e)}
          >
            <PTable caption={t("accordion.loc.caption") || "Loading..."}>
              <PTableBody>
                {(locations ?? [])
                  .slice() // no mutar el estado original
                  .sort((a, b) => {
                    const pa = Number(
                      a?.pivot?.position ?? a?.position ?? Infinity
                    );
                    const pb = Number(
                      b?.pivot?.position ?? b?.position ?? Infinity
                    );
                    return pa - pb;
                  })
                  .map((location) => (
                    <PTableRow key={location.locationUUID ?? location.id}>
                      <PTableCell>{location.name}</PTableCell>
                    </PTableRow>
                  ))}
              </PTableBody>
            </PTable>
          </PAccordion>
        )}

        {/* Accordion de Punto de Recogida */}
        {!optionalFields?.custom_pick_up && (
          <PAccordion
            heading={t("accordion.pickup.head") || "Loading..."}
            open={accordionStates[6]}
            onUpdate={(e) => handleAccordionUpdate(6, e)}
          >
            <PTable caption={t("accordion.pickup.caption") || "Loading..."}>
              <PTableBody>
                {/* {Object.keys(pickupContent).map((key, index) => ( */}
                <PTableRow>
                  <PTableCell multiline={true}>
                    {t("accordion.pickup.content.1") || "Loading..."}
                  </PTableCell>
                </PTableRow>
                <PTableRow>
                  <PTableCell multiline={true}>
                    {t("accordion.pickup.content.2") || "Loading..."}
                    {t("accordion.pickup.content.3") || "Loading..."}
                  </PTableCell>
                </PTableRow>
                {/* ))} */}
              </PTableBody>
            </PTable>
          </PAccordion>
        )}

        {/* Accordion de Información Importante */}
        {optionalFields?.legal_default && (
          <PAccordion
            heading={t("accordion.info.head") || "Loading..."}
            open={accordionStates[3]}
            onUpdate={(e) => handleAccordionUpdate(3, e)}
          >
            <PTable caption={t("accordion.info.caption") || "Loading..."}>
              <PTableBody>
                {Object.keys(infoContent).map((key, index) => (
                  <PTableRow key={key}>
                    <PTableCell multiline={true}>
                      {infoContent[key] || "Loading..."}
                    </PTableCell>
                  </PTableRow>
                ))}
              </PTableBody>
            </PTable>
          </PAccordion>
        )}

        {/* Accordion de Política de Cancelación */}
        {optionalFields?.cancel_policy_default && (
          <PAccordion
            heading={t("accordion.legal.head") || "Loading..."}
            open={accordionStates[4]}
            onUpdate={(e) => handleAccordionUpdate(4, e)}
          >
            <PTable caption={t("accordion.legal.caption") || "Loading..."}>
              <PTableBody>
                {Object.keys(legalContent).map((key, index) => (
                  <PTableRow key={key}>
                    <PTableCell multiline={true}>
                      {legalContent[key] || "Loading..."}
                    </PTableCell>
                  </PTableRow>
                ))}
              </PTableBody>
            </PTable>
          </PAccordion>
        )}

        {/* Accordion de Información Adicional */}
        <PAccordion
          heading={t("accordion.further.head") || "Loading..."}
          open={accordionStates[5]}
          onUpdate={(e) => handleAccordionUpdate(5, e)}
        >
          <PTable caption={t("accordion.further.caption") || "Loading..."}>
            <PTableBody>
              <PTableRow>
                <PTableCell multiline={false}>
                  <p className="text-sm text-gray-700">
                    {t("accordion.further.content") || "Loading..."}
                  </p>
                </PTableCell>
              </PTableRow>

              {/* Teléfono */}
              <PTableRow>
                <PTableCell>
                  <div className="flex items-center space-x-3">
                    <Phone className="h-5 w-5 text-gray-500" />
                    &nbsp;
                    <a
                      href={`tel:${normalizeForTel(
                        userData?.data?.contact_number ?? ""
                      )}`}
                      aria-label={t("accessibility.call", {
                        value: userData?.data?.contact_number ?? "",
                      })}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {/* Muestra con espacios tal cual venga del backend */}
                      {userData?.data?.contact_number}
                    </a>
                  </div>
                </PTableCell>
              </PTableRow>

              {/* WhatsApp */}
              <PTableRow>
                <PTableCell>
                  <div className="flex items-center space-x-3">
                    <FaWhatsapp className="h-5 w-5 text-gray-500" />
                    &nbsp;
                    <a
                      href={`https://wa.me/${normalizeForWa(
                        userData?.data?.contact_number ?? ""
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t("accessibility.whatsapp", {
                        value: userData?.data?.contact_number ?? "",
                      })}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {userData?.data?.contact_number}
                    </a>
                  </div>
                </PTableCell>
              </PTableRow>

              {/* Email */}
              <PTableRow>
                <PTableCell>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-5 w-5 text-gray-500" />
                    &nbsp;
                    <a
                      href={`mailto:${normalizeEmail(
                        userData?.data?.contact_email ?? ""
                      )}`}
                      aria-label={t("accessibility.email", {
                        value: userData?.data?.contact_email ?? "",
                      })}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {userData?.data?.contact_email}
                    </a>
                  </div>
                </PTableCell>
              </PTableRow>
            </PTableBody>
          </PTable>
        </PAccordion>
      </div>
    </div>
  );
};

export default InformationDescriptionSide;
